import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { array, number, bool, string, element, func } from "prop-types";
import get from "lodash/get";

import { SliderArrow } from "./SliderArrow";
import { SliderIndicator } from "./SliderIndicator";
import { getCategoryAndSubCategory, getTemplateName } from "../../../../ga-utils";

import "./scroll-snap.m.css";

const smoothScroll = (node, topOrLeft, horizontal) => {
  node.scrollTo({
    [horizontal ? "left" : "top"]: topOrLeft,
    behavior: "smooth",
  });
};

const updateItemSelection = (index, languageDirection, scroller, noOfItems) => {
  const getScrollValue = Math.floor(scroller.current.scrollWidth * (index / noOfItems));
  const scrollValue = languageDirection === "rtl" ? getScrollValue * -1 : getScrollValue;
  smoothScroll(scroller.current, scrollValue, true);
};

const setIndicatorValue = (func, ms) => {
  let timeout;

  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      func();
    }, ms);
  };
};

export const ScrollSnap = ({
  children,
  isArrow,
  interval,
  isInfinite,
  pauseOnHover,
  perView,
  slideIndicator,
  previousIcon,
  nextIcon,
  defaultIndex = 0,
  autoPlay,
  trackButtonClick,
  isUrlChangeNeeded,
  story,
  config,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  const [autoScroll, setAutoScroll] = useState(false);
  const languageDirection = useSelector((state) => get(state, ["qt", "config", "language", "direction"], "ltr"));
  const noOfItems = children ? children.length : 0;
  const scroller = useRef(null);

  const numberOfIndicatorsToShow = children.length - (perView - 1);
  const indicatorItems = children.slice(0, numberOfIndicatorsToShow);

  useEffect(() => {
    if (scroller.current) {
      scroller.current.addEventListener(
        "scroll",
        setIndicatorValue(() => {
          const value = Math.round((scroller.current.scrollLeft / scroller.current.scrollWidth) * noOfItems);
          setSelectedIndex(Math.abs(value));
        }, 150)
      );
      // For Default Selection if selectedIndex is not 0 then manually select it
      if (selectedIndex !== 0) {
        const scrollValue = (scroller.current.scrollWidth / noOfItems) * selectedIndex;
        scroller.current.scrollLeft = scrollValue;
      }
    }
    return () => {
      scroller.current.removeEventListener("scroll", setIndicatorValue);
    };
  }, []);

  // Infinite scroll
  useEffect(() => {
    if (isInfinite && !autoScroll) {
      const timer = setInterval(() => {
        infiniteScroll();
      }, interval);

      return () => {
        clearInterval(timer);
      };
    }
  }, [isInfinite, autoScroll]);

  const infiniteScroll = () => {
    setSelectedIndex((value) => {
      const newIndex = value === noOfItems - perView ? 0 : value + 1;
      updateItemSelection(newIndex, languageDirection, scroller, noOfItems);
      return newIndex;
    });
  };

  const pauseAutoPlay = () => {
    autoPlay && pauseOnHover && setAutoScroll(!autoScroll);
  };

  const indicatorClick = (e, newIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedIndex(newIndex);
    updateItemSelection(newIndex, languageDirection, scroller, noOfItems);
  };

  const handleGAEvent = (index, action, linkText) => {
    if (typeof window === "undefined") return;

    const pageType = "story-page";
    const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);

    window.dataLayer.push({
      event: "dl_page_info",
      photo_index: index + 1,
      event_action: action,
      link_text: linkText,
      category,
      sub_category: subCategory,
      content_id: contentId,
      template_name: getTemplateName(story?.["story-template"] || pageType),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
  };

  const nextClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (selectedIndex < noOfItems) {
      // Track the next button click if tracking function is provided
      if (typeof trackButtonClick === "function") {
        trackButtonClick("Next");
      }
      const newIndex = selectedIndex + 1;
      if (typeof window !== "undefined" && window && isUrlChangeNeeded) {
        const url = new URL(window.location);
        url.searchParams.set("photo", newIndex + 1); // 1-based index
        window.history.replaceState({}, "", url);
        handleGAEvent(newIndex, "photo_maxview_button_click", "next");
      }
      setSelectedIndex(newIndex);
      updateItemSelection(newIndex, languageDirection, scroller, noOfItems);
    }
  };

  const previousClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Track the previous button click if tracking function is provided
    if (typeof trackButtonClick === "function") {
      trackButtonClick("Prev");
    }
    const newIndex = selectedIndex - 1;

    if (typeof window !== "undefined" && window && isUrlChangeNeeded) {
      const url = new URL(window.location);
      url.searchParams.set("photo", newIndex + 1); // 1-based index
      window.history.replaceState({}, "", url);
      handleGAEvent(newIndex, "photo_maxview_button_click", "previous");
    }
    setSelectedIndex(newIndex);
    updateItemSelection(newIndex, languageDirection, scroller, noOfItems);
  };

  return (
    <div className="scroll-snap-container">
      <div
        styleName="wrapper"
        className="scroll-snap-wrapper"
        onMouseEnter={pauseAutoPlay}
        onMouseLeave={pauseAutoPlay}
      >
        <div ref={scroller} styleName="carousel" className="scroll-snap-carousel">
          {children}
        </div>
        {isArrow && (
          <SliderArrow
            noOfItems={noOfItems}
            perView={perView}
            previousClick={previousClick}
            nextClick={nextClick}
            selectedIndex={selectedIndex}
            languageDirection={languageDirection}
            previousIcon={previousIcon}
            nextIcon={nextIcon}
          />
        )}
      </div>

      {children.length > perView && slideIndicator !== "none" && (
        <SliderIndicator
          indicatorItems={indicatorItems}
          indicatorClick={indicatorClick}
          selectedIndex={selectedIndex}
          indicatorType={slideIndicator}
        />
      )}
    </div>
  );
};

ScrollSnap.defaultProps = {
  isArrow: true,
  isInfinite: false,
  interval: 4000,
  pauseOnHover: true,
  perView: 1,
  autoPlay: true,
  slideIndicator: "dots",
};

ScrollSnap.propTypes = {
  children: array.isRequired,
  isArrow: bool,
  isInfinite: bool,
  autoPlay: bool,
  defaultIndex: number,
  interval: number,
  pauseOnHover: bool,
  perView: number,
  slideIndicator: string,
  previousIcon: element,
  nextIcon: element,
  trackButtonClick: func,
};
